import React, { useCallback, useEffect, useState } from "react";
import { Eye, History, Pencil, UserPlus, Undo2 } from "lucide-react";
import toast from "react-hot-toast";
import { DsBadge, DsButton, DsSpinner } from "../../../design-system";
import { assetService } from "../../../services/api";
import AssetAssignModal from "./AssetAssignModal";
import AssetFormModal from "./AssetFormModal";
import AssetHistoryDrawer from "./AssetHistoryDrawer";
import { ASSET_STATUSES, inputSm, statusLabel } from "./constants";

function normalizeList(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.assets)) return d.assets;
  if (Array.isArray(d?.rows)) return d.rows;
  if (Array.isArray(res?.assets)) return res.assets;
  return [];
}

export default function AssetRegisterPanel({
  canCreate,
  canEdit,
  canAssign,
  initialFilters = {},
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(initialFilters.status || "all");
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("assign");
  const [assignAsset, setAssignAsset] = useState(null);
  const [historyAsset, setHistoryAsset] = useState(null);

  const loadMasters = useCallback(async () => {
    try {
      const [catRes, typeRes] = await Promise.all([
        assetService.listCategories(),
        assetService.listTypes(),
      ]);
      setCategories(catRes?.data || catRes || []);
      setTypes(typeRes?.data || typeRes || []);
    } catch {
      setCategories([]);
      setTypes([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status && status !== "all") params.status = status;
      const res = await assetService.listAssets(params);
      setRows(normalizeList(res));
    } catch (err) {
      setRows([]);
      toast.error(err?.response?.data?.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (initialFilters.status) setStatus(initialFilters.status);
  }, [initialFilters.status]);

  const openAssign = (asset, mode) => {
    if (!canAssign) {
      toast.error("No permission to assign assets");
      return;
    }
    setAssignAsset(asset);
    setAssignMode(mode);
    setAssignOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Asset Register</h1>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Search, filter, and manage company assets.
          </p>
        </div>
        {canCreate ? (
          <DsButton
            type="button"
            onClick={() => {
              setEditAsset(null);
              setFormOpen(true);
            }}
          >
            Add Asset
          </DsButton>
        ) : null}
      </div>

      <div className="ds-page-toolbar flex flex-wrap items-center gap-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
        <input
          className={`${inputSm} max-w-xs`}
          placeholder="Search tag, name, serial…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className={`${inputSm} max-w-[11rem]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          {ASSET_STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <DsButton type="button" variant="outline" onClick={load}>Refresh</DsButton>
      </div>

      <div className="ds-page-panel overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
        {loading ? (
          <div className="flex justify-center py-12"><DsSpinner /></div>
        ) : rows.length === 0 ? (
          <p className="ds-caption p-6 text-[hsl(var(--ds-muted-fg))]">No assets found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-left">
                  <th className="px-3 py-2 ds-caption">Asset</th>
                  <th className="px-3 py-2 ds-caption">Assignment</th>
                  <th className="px-3 py-2 ds-caption">Status</th>
                  <th className="px-3 py-2 ds-caption">Location</th>
                  <th className="px-3 py-2 ds-caption">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const tag = row.tag || row.assetTag || row.asset_tag || "—";
                  const assignee =
                    row.assignedToName ||
                    row.assigned_to_name ||
                    row.employeeName ||
                    "—";
                  return (
                    <tr key={row.id} className="border-b border-[hsl(var(--ds-border))] last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-[hsl(var(--ds-fg))]">{row.name}</div>
                        <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                          {tag}
                          {row.serialNumber || row.serial_number
                            ? ` · SN ${row.serialNumber || row.serial_number}`
                            : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[hsl(var(--ds-fg))]">{assignee}</td>
                      <td className="px-3 py-2">
                        <DsBadge>{statusLabel(row.status)}</DsBadge>
                      </td>
                      <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                        {row.location || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            title="View / History"
                            className="rounded p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                            onClick={() => setHistoryAsset(row)}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {canEdit ? (
                            <button
                              type="button"
                              title="Edit"
                              className="rounded p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                              onClick={() => {
                                setEditAsset(row);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : null}
                          {canAssign ? (
                            <>
                              <button
                                type="button"
                                title="Assign"
                                className="rounded p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                                onClick={() => openAssign(row, "assign")}
                              >
                                <UserPlus className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                title="Return"
                                className="rounded p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                                onClick={() => openAssign(row, "return")}
                              >
                                <Undo2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            title="History"
                            className="rounded p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                            onClick={() => setHistoryAsset(row)}
                          >
                            <History className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AssetFormModal
        open={formOpen}
        asset={editAsset}
        categories={Array.isArray(categories) ? categories : []}
        types={Array.isArray(types) ? types : []}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />
      <AssetAssignModal
        open={assignOpen}
        mode={assignMode}
        asset={assignAsset}
        onClose={() => setAssignOpen(false)}
        onSaved={load}
      />
      <AssetHistoryDrawer
        open={Boolean(historyAsset)}
        asset={historyAsset}
        onClose={() => setHistoryAsset(null)}
      />
    </div>
  );
}
